<template>
  <section class="rbs-step" aria-labelledby="rbs-heading" data-testid="readiness-badges-step">
    <div class="rbs-heading">
      <h2 id="rbs-heading" class="rbs-title" data-testid="rbs-title">{{ title }}</h2>
    </div>

    <div class="rbs-content">
      <p v-if="introText" class="rbs-intro" data-testid="rbs-intro">{{ introText }}</p>

      <ul v-if="levels.length" class="rbs-levels" data-testid="rbs-levels">
        <li v-for="(level, index) in levels" :key="index" class="rbs-level" data-testid="rbs-level">
          <span
            class="rbs-dot"
            :style="{ backgroundColor: level.color || '#7f56d9' }"
            aria-hidden="true"
            data-testid="rbs-dot"
          ></span>
          <span class="rbs-level-text">{{ level.text }}</span>
        </li>
      </ul>

      <p v-if="noviceText" class="rbs-novice" data-testid="rbs-novice">{{ noviceText }}</p>
    </div>

    <div class="rbs-spacer" aria-hidden="true"></div>

    <div class="rbs-actions">
      <button
        type="button"
        class="rbs-btn rbs-btn--primary"
        data-testid="rbs-continue"
        @click="handleContinue"
      >
        {{ buttonLabel }}
      </button>
    </div>
  </section>
</template>

<script setup>
/**
 * ER-12245 — ReadinessBadgesStep (MDP-621).
 *
 * "Earn your readiness badges" — the final page of the Employee and
 * Admin + Employee wizards. Lists the readiness levels with their badge colour
 * and the Novice note, then the flow's final button ("Go to your
 * responsibilities" for Employee, "Go to your organization" for
 * Admin + Employee). Presentational only: copy, levels and the button label
 * arrive as props, and the component emits `continue`.
 */
defineProps({
  /** Step title. */
  title: {
    type: String,
    default: "Earn your readiness badges",
  },
  /** Intro paragraph. */
  introText: {
    type: String,
    default:
      "Every responsibility assigned to you has its own badge. The badge level shows how ready you are for that responsibility.",
  },
  /** Readiness levels: `{ text, color }` (color is the badge dot). */
  levels: {
    type: Array,
    default: () => [],
  },
  /** The Novice note shown beneath the levels. */
  noviceText: {
    type: String,
    default: "Until you earn a badge for a responsibility, you are a Novice for it.",
  },
  /** Final action label. */
  buttonLabel: {
    type: String,
    default: "Go to your responsibilities",
  },
});

const emit = defineEmits(["continue"]);

function handleContinue() {
  emit("continue");
}
</script>

<style scoped>
.rbs-step {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  height: 100%;
  padding: 0;
  background: #ffffff;
  color: #101828;
  font-family: Inter, sans-serif;
}

.rbs-heading {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.rbs-title {
  margin: 0;
  color: #101828;
  font-size: 28px;
  font-weight: 500;
  line-height: 34px;
}

.rbs-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.rbs-intro {
  margin: 0;
  color: #475467;
  font-size: 16px;
  line-height: 24px;
}

.rbs-levels {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.rbs-level {
  display: flex;
  align-items: center;
  gap: 12px;
}

.rbs-dot {
  flex: 0 0 auto;
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.rbs-level-text {
  color: #101828;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
}

.rbs-novice {
  margin: 0;
  color: #667085;
  font-size: 14px;
  line-height: 20px;
}

.rbs-spacer {
  flex: 1 1 auto;
}

.rbs-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rbs-btn {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  min-width: 120px;
  padding: 0 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
}

.rbs-btn--primary {
  background: #7f56d9;
  border: 1px solid #7f56d9;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.rbs-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}
</style>
